import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, CostCentre } from '../../api/finance';
import { departmentsApi } from '../../api/admin/departments';
import { locationsApi } from '../../api/admin/locations';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Power } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

export function CostCentresPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const costCentresQuery = useQuery({ queryKey: ['finance-cost-centres'], queryFn: () => financeApi.listCostCentres() });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list(), enabled: showForm });
  const locationsQuery = useQuery({ queryKey: ['admin-locations'], queryFn: () => locationsApi.list(), enabled: showForm });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-cost-centres'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createCostCentre({
      costCentreCode: v.costCentreCode, name: v.name, description: v.description || undefined,
      departmentId: v.departmentId ? Number(v.departmentId) : undefined, locationId: v.locationId ? Number(v.locationId) : undefined,
    }),
    onSuccess: () => { showSuccess('Cost centre created.'); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create cost centre.'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'inactive' }) => financeApi.setCostCentreStatus(id, status),
    onSuccess: () => { showSuccess('Updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <PageHeader title="Cost Centres" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Create Cost Centre'}</button>} />

      {showForm && (
        <section className="card">
          <h2>New Cost Centre</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Cost Centre Code<input {...register('costCentreCode', { required: true })} /></label>
            <label>Name<input {...register('name', { required: true })} /></label>
            <label>Description<textarea rows={2} {...register('description')} /></label>
            <label>
              Department
              <select {...register('departmentId')}>
                <option value="">- select -</option>
                {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Location
              <select {...register('locationId')}>
                <option value="">- select -</option>
                {locationsQuery.data?.data.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Cost Centres" />
        <div className="panel-body">
        {costCentresQuery.data && (
          <DataTable<CostCentre>
            rows={costCentresQuery.data.data}
            rowKey={(c) => c.id}
            emptyMessage="No cost centres yet."
            columns={[
              { header: 'Code', render: (c) => c.cost_centre_code },
              { header: 'Name', render: (c) => c.name },
              { header: 'Department', render: (c) => c.department_name ?? '-' },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} /> },
              { header: '', render: (c) => <IconActionButton icon={Power} label={c.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => toggleStatusMutation.mutate({ id: c.id, status: c.status === 'active' ? 'inactive' : 'active' })} /> },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}
